import React, { memo, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { change, FormSection } from 'redux-form'
import { every, flatMap, keys, some } from 'lodash-es'
import { Translate } from 'react-localize-redux'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { Checkbox } from 'antd'
import PermissionGroup from './PermissionGroup'
import { checkUncheckLicensePermissions } from './Permissions.helpers'
import { PermissionsProps } from './Permissions.model'
import { FormMode } from 'shared/interfaces'
import styles from './Permissions.module.scss'

const Permissions = memo(function (props: PermissionsProps) {
  const { currentPermissions, profilePermissions, editingDisabled, keylockerEnabled, allPermissions } = props
  const [currentPermissionState, setCurrentPermisisonState] = useState(currentPermissions)

  useEffect(() => {
    if(currentPermissions) {
      const permissions = checkUncheckLicensePermissions(profilePermissions, currentPermissions)
      setCurrentPermisisonState(permissions)
    }
  }, [currentPermissions])

  const onGroupCheckboxChange = (
    newPermissions: Record<string, boolean>
  ): void => {
    props.change(props.formName, 'permissions', {
      ...currentPermissionState,
      ...newPermissions
    })
  }

  const isGroupChecked = (groupName: string): boolean => {
    return (
      allPermissions[groupName].length > 0 &&
      every(
        allPermissions[groupName],
        (p) => currentPermissionState && currentPermissionState[p.id]
      )
    )
  }

  const isGroupIndeterminate = (groupName: string): boolean => {
    return (
      !isGroupChecked(groupName) &&
      some(
        allPermissions[groupName],
        (p) => currentPermissionState && currentPermissionState[p.id]
      )
    )
  }

  const selectAll = (event: CheckboxChangeEvent): void => {
    const newPermissions = flatMap(allPermissions).reduce(
      (res, permission) => ({ ...res, [permission.id]: event.target.checked }),
      {}
    )

    props.change(props.formName, 'permissions', {
      ...currentPermissionState,
      ...newPermissions
    })
  }

  const iAllGroupsSelected = (): boolean => {
    const newPermissions = flatMap(allPermissions)
    return every(
      newPermissions,
      (p) => currentPermissionState && currentPermissionState[p.id]
    )
  }

  if (props.mode === FormMode.edit && !props.canManagePermission) {
    return <p>You have no permission to manage permissions</p>
  } else {
    return (
      <FormSection name='permissions'>
        <h2 className='permissionTitle'>
          <Translate id='manageUser.administrators.form.selectPermissionTitle' />
        </h2>
        <p className='permissionDescription'>
          <Translate id='manageUser.administrators.form.selectPermissionDescription' />
        </p>

        {!editingDisabled && (
          <div className={styles.selectAllWrap}>
            <Checkbox
              checked={iAllGroupsSelected()}
              className={styles.checkGroup}
              onChange={selectAll}
              disabled={editingDisabled}
            >
              <h3 className={styles.groupName}>
                <Translate id='manageUser.administrators.form.selectAll' />
              </h3>
            </Checkbox>
          </div>
        )}

        <div>
          {keys(props.allPermissions).map((groupName) => (
            <PermissionGroup
              key={groupName}
              mode={props.mode}
              groupName={groupName}
              groupPermissions={allPermissions[groupName]}
              profilePermissions={profilePermissions}
              onGroupCheckboxChange={onGroupCheckboxChange}
              isGroupChecked={isGroupChecked(groupName)}
              isGroupIndeterminate={isGroupIndeterminate(groupName)}
              currentAccessScope={props.currentAccessScope}
              profileAccessScope={props.profileAccessScope}
              currentPermisisons={currentPermissionState}
              featureIds={props.featureIds}
              editingDisabled={!editingDisabled}
              keylockerEnabled={keylockerEnabled}
            />
          ))}
        </div>
      </FormSection>
    )
  }
})

Permissions.displayName = 'Permissions'

export default connect(null, { change })(Permissions)
